/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import authActions from '@sportninja/common/actions/auth'
import userActions from '@sportninja/common/actions/user'
import accountActions from '@sportninja/common/actions/account'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { NavLink } from 'react-router-dom'

import { SearchContext } from '../../pages/Search/SearchContextProvider'
import Chat from '../Chat'
import EnvSelect from '../EnvSelect'
import { Flex } from '../Layout'
import Notifications from '../Notifications'
import { Desktop, Mobile } from '../Responsive'
import { font, MENU_HEIGHT } from '../css'
import MenuIcon from './MenuIcon'
import { CloseCircleOutlined } from '@ant-design/icons'
import MenuHamburgerIcon from '../icons/MenuHamburgerIcon'
import NotificationIcon from '../icons/NotificationIcon'
import QuestionIcon from '../icons/QuestionIcon'
import SportNinjaTextLogo from '../icons/SportNinjaTextLogo'
import CanlanTextLogo from '../icons/CanlanTextLogo'
import CanlanLogoIcon from '../icons/CanlanLogoIcon'
import req from '@sportninja/common/api/request'
import { Account } from '@sportninja/common/types/Account'
import { Profile } from './Profile'
import { SearchIcon } from './SearchIcon'
import { Header, Logo, Wrap } from './css'
import LogoSvg from './logo.svg'
import useWindowSize from 'src/hooks/useWindowSize'

const LogoIcon = () => (
  <img
    src={LogoSvg}
    alt='SportNinja Logo'
    css={css`
      width: 100%;
      height: 100%;
    `}
  />
)

interface User {
  id?: string
  email?: string
  imageId?: string
  isValidated?: boolean
  account_id?: number
  account_type?: number
  name_first?: string
  name_last?: string
  image?: { id: string }
}

interface Location {
  pathname: string
  search?: string
}

interface MainNavProps {
  minimal?: boolean
  user?: User
  readUser: () => Promise<void>
  readAccount: (id: string) => Promise<void>
  account?: Account
  location: Location
  impersonate: (payload: { email: string }) => Promise<void>
  logout: () => Promise<void>
  onSidebarToggle?: () => void
  sidebarCollapsed?: boolean
}

interface NotificationCount {
  unread: number
}

const MainNav: React.FC<MainNavProps> = ({
  minimal = false,
  user,
  readUser,
  readAccount,
  account,
  location,
  impersonate,
  logout,
  onSidebarToggle,
  sidebarCollapsed = false,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])
  const searchContext = useContext(SearchContext)
  const menuHeight = MENU_HEIGHT
  const [notificationsCount, setNotificationsCount] =
    useState<NotificationCount | null>(null)

  const isPaymentPage = location?.pathname?.includes('pay')
  const isRegistrationInvitePage =
    location?.pathname?.startsWith('/registration/') &&
    !location?.pathname?.startsWith('/registration-details/')
  const isLoginPage = location?.pathname?.includes('/login')
  const isRegisterPage = location?.pathname?.includes('/signup')
  const isTestPage = location?.pathname?.includes('/ant-test')
  const isForgotPasswordPage = location?.pathname?.includes('/forgot-password')
  const isVerifyEmailPage = location?.pathname?.includes('/verify-email')
  const isPasswordResetPage = location?.pathname?.includes('/password-reset')
  const firstRender = useRef(true)

  const readNotificationsCount = async () => {
    if (!user) {
      return
    }
    if (location?.pathname?.includes('public')) {
      return
    }
    try {
      const result = await req('/notifications/count_unread')
      if (result && result?.notifications) {
        setNotificationsCount(result?.notifications)
      }
    } catch (error) {
      // Silent fail - notification count is non-critical
    }
  }

  useEffect(() => {
    if (firstRender.current) {
      readNotificationsCount()
      firstRender.current = false
    }
  }, [])

  useEffect(() => {
    if (!minimal) {
      readUser()
    }
  }, [minimal])

  useEffect(() => {
    if (user?.account_id && !isCanlan && !minimal) {
      readAccount(String(user.account_id)).catch(() => {
        // Silent fail - account fetch is non-critical
      })
    }
  }, [readAccount, account, user, minimal])

  // Early return if SearchContext not available
  if (!searchContext) {
    return <EnvSelect user={user} impersonate={impersonate} />
  }

  const getSubscriptionBanner = () => {
    if (isCanlan || !account?.subscription_type) {
      return null
    }

    const subscriptionTypeId = account.subscription_type.id
    const subscriptionName = account.subscription_type.name

    const isFree = subscriptionTypeId === 1
    const isPaid = subscriptionTypeId === 2 || subscriptionTypeId === 3

    if (!isFree && !isPaid) {
      return null
    }

    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          padding: 4px 8px;
          border-radius: 4px;
          background-color: ${isFree
            ? colors.SECONDARY_200
            : 'rgba(242, 180, 0, 0.10)'};
          ${font.body}
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 600;
          line-height: 14.4px;
          letter-spacing: 1.44px;
          text-transform: uppercase;
          color: ${isFree ? colors.NEUTRAL_30 : colors.PRIMARY};
        `}
      >
        {subscriptionName}
      </div>
    )
  }

  const logoLink = (
    <NavLink
      to={ROUTES.HOME}
      title={`${isCanlan ? 'Canlan Stats' : 'SportNinja'} | Home`}
      css={css`
        display: flex;
        align-items: center;
        gap: 8px;
      `}
    >
      {isCanlan ? (
        isMobile ? (
          <CanlanLogoIcon height={24} />
        ) : (
          <CanlanTextLogo height={24} />
        )
      ) : (
        <>
          <Logo>
            <LogoIcon />
          </Logo>
          {!isMobile ? <SportNinjaTextLogo height={24} /> : null}
          {getSubscriptionBanner()}
        </>
      )}
    </NavLink>
  )

  const inviteCountComponent = notificationsCount &&
    notificationsCount?.unread > 0 && (
      <div
        css={css`
          position: absolute;
          top: -10px;
          right: -10px;
          height: 18px;
          width: 18px;
          border-radius: 50%;
          background-color: ${colors.ERROR_300};
          padding-bottom: 1px;
        `}
      >
        <div
          css={css`
            color: ${colors.WHITE};
            ${font.title};
            font-size: 13px;
            font-weight: 700;
            text-align: center;
            letter-spacing: 1.2px;
          `}
        >
          {notificationsCount?.unread}
        </div>
      </div>
    )

  const hasSearchText = (searchContext as any).searchText.length > 0

  if (
    isPaymentPage ||
    isLoginPage ||
    isRegistrationInvitePage ||
    isRegisterPage ||
    isForgotPasswordPage ||
    isVerifyEmailPage ||
    isPasswordResetPage ||
    isTestPage
  ) {
    return <EnvSelect user={user} impersonate={impersonate} />
  }

  return (
    <Wrap {...({ menuHeight } as any)}>
      <EnvSelect user={user} impersonate={impersonate} />
      <Header {...({ menuHeight, alignItems: 'center' } as any)} as='header'>
        <Flex
          {...({ alignItems: 'center' } as any)}
          as='nav'
          aria-labelledby='site-menu'
        >
          {/* Hamburger menu for sidebar toggle */}
          <Desktop>
            {onSidebarToggle && (
              <button
                type='button'
                onClick={onSidebarToggle}
                css={css`
                  background: transparent;
                  border: none;
                  cursor: pointer;
                  padding: 8px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  margin-right: 16px;
                  transition: all 0.2s;

                  &:hover {
                    background-color: ${colors.SECONDARY_400};
                    border-radius: 4px;
                  }
                `}
                aria-label={
                  sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
                }
              >
                <MenuHamburgerIcon size={20} color={colors.NEUTRAL_50} />
              </button>
            )}
          </Desktop>

          <Mobile>{() => logoLink}</Mobile>

          <div
            className='main-nav-search-container'
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
              position: absolute;
              left: 50%;
              transform: translateX(-50%);

              @media (max-width: 768px) {
                position: relative;
                left: auto;
                transform: none;
                margin-left: 0;
              }
            `}
          >
            <div
              css={css`
                position: relative;
                display: flex;
                align-items: center;
                width: 600px;
                ${isMobile
                  ? css`
                      width: 100%;
                    `
                  : null}
              `}
            >
              <div
                css={css`
                  position: absolute;
                  left: 12px;
                  pointer-events: none;
                  z-index: 1;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  ${isMobile
                    ? css`
                        left: 24px;
                      `
                    : null}
                `}
              >
                <SearchIcon
                  size={isMobile ? 16 : 24}
                  color={colors.NEUTRAL_50}
                />
              </div>
              <input
                ref={(searchContext as any).searchInputRef}
                type='text'
                className='main-nav-search-input'
                onFocus={() => {
                  ;(searchContext as any).setIsActive(true as boolean)
                }}
                onChange={({ target }) => {
                  ;(searchContext as any).setText(target.value)
                  // Ensure search panel shows when typing
                  if (target.value.length > 0) {
                    ;(searchContext as any).setIsActive(true as boolean)
                  }
                }}
                value={(searchContext as any).searchText}
                placeholder={isMobile ? 'Search' : 'Search Here...'}
                css={css`
                  width: 100%;
                  height: 36px;
                  padding: 4px 12px 4px 48px;
                  background: #212935;
                  border: 1px solid ${colors.SECONDARY_200};
                  border-radius: 16px;
                  color: ${colors.WHITE};
                  ${font.body}
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 20.3px;

                  &::placeholder {
                    color: ${colors.NEUTRAL_100};
                  }

                  &:focus {
                    outline: none;
                    border-color: ${colors.SECONDARY_75};
                  }
                  ${isMobile
                    ? css`
                        width: 100%;
                        height: 32px;
                        margin-left: 12px;
                      `
                    : null}
                `}
              />
              <Desktop>
                {hasSearchText && (
                  // <button
                  //   type='button'
                  //   css={css`
                  //     position: absolute;
                  //     right: 12px;
                  //     cursor: pointer;
                  //     background: transparent;
                  //     border: none;
                  //     ${font.body}
                  //     color: ${colors.DEFAULT_FLAIR};
                  //     font-size: 14px;
                  //     padding: 0;
                  //     z-index: 1;
                  //   `}
                  // onClick={(e) => {
                  //   e.preventDefault()
                  //   e.stopPropagation()
                  //   ;(searchContext as any).setText('')
                  //   ;(searchContext as any).searchInputRef.current?.focus()
                  // }}
                  // >
                  //   Clear
                  // </button>
                  <CloseCircleOutlined
                    css={css`
                      position: absolute;
                      right: 12px;
                      cursor: pointer;
                      font-size: 20px;
                      color: ${colors.NEUTRAL_50};
                      z-index: 1;
                    `}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      ;(searchContext as any).setText('')
                      ;(searchContext as any).searchInputRef.current?.focus()
                    }}
                  />
                )}
                {((searchContext as any).isActive as boolean) &&
                  !hasSearchText && (
                    <CloseCircleOutlined
                      css={css`
                        position: absolute;
                        right: 12px;
                        cursor: pointer;
                        font-size: 20px;
                        color: ${colors.NEUTRAL_50};
                        z-index: 1;
                      `}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        ;(searchContext as any).setIsActive(false as boolean)
                      }}
                    />
                  )}
              </Desktop>
            </div>
          </div>
        </Flex>
        <Mobile>
          {() => {
            return (
              !minimal && (
                <Flex
                  {...({
                    row: true,
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  } as any)}
                >
                  {/* Removed horizontal navigation links - now in sidebar */}

                  <Chat
                    loggedInUserId={user?.id}
                    isTeamOrSchedulePage={false}
                  />
                  <Notifications
                    refreshNotificationCount={() => {
                      readNotificationsCount()
                    }}
                    Button={(toggle) => (
                      <MenuIcon
                        name='bell'
                        onClick={toggle}
                        title={t('Web:viewInvitations', {})}
                        css={css`
                          margin-right: 16px;
                          margin-top: 4px;
                        `}
                        {...({
                          customIcon: (
                            <NotificationIcon
                              size={24}
                              color={colors.NEUTRAL_50}
                            />
                          ),
                        } as any)}
                      >
                        {inviteCountComponent}
                      </MenuIcon>
                    )}
                  />
                  <a
                    href={
                      isCanlan
                        ? 'https://faq.canlansports.com/faq-category/canlan-stats-app'
                        : 'https://support.sportninja.com/'
                    }
                    rel='noreferrer'
                    target='_blank'
                    css={css`
                      margin-right: 16px;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      margin-top: 4px;
                    `}
                  >
                    <MenuIcon
                      title='Support Documentation'
                      name='question-circle'
                      css={css``}
                      {...({
                        customIcon: (
                          <QuestionIcon size={24} color={colors.NEUTRAL_50} />
                        ),
                      } as any)}
                    />
                  </a>
                  <Profile
                    onLogoutClick={logout}
                    user={user}
                    isMobile={isMobile}
                  />
                </Flex>
              )
            )
          }}
        </Mobile>
      </Header>
    </Wrap>
  )
}

const mapStateToProps = (state: any) => {
  const accountState = state.account || {}
  // Find account by searching through all accounts
  // Since we fetch by account_id but store by uid, we need to find it
  const accountEntries = Object.values(accountState) as Account[]
  const account = accountEntries.length > 0 ? accountEntries[0] : undefined

  return {
    account,
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    readUser: bindActionToPromise(dispatch, userActions.read.request),
    readAccount: bindActionToPromise(dispatch, accountActions.read.request),
    impersonate: bindActionToPromise(dispatch, authActions.impersonate.request),
    logout: bindActionToPromise(dispatch, authActions.logout.request),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MainNav)
